import { isFunction } from 'lodash';
import { useEffect, useRef } from 'react';

import { useCurrentDate } from '@/hooks/current-date-provider';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { TimelineItemModel } from '@/lib/models/timeline-item/types';
import { TimelineScaleTypes } from '@/types';
import { tv } from '@/utils/styles';

import { TimelineRangeBar } from './range-bar';
import { TimelineRow } from './row';
import { getTimelineGridSize, getTimelineRange } from './utils';

export interface TimelineProps {
  items: TimelineItemModel[];
  scale?: TimelineScaleTypes;
  onRowClick?: (shipment: ShipmentModel) => void;
}

const Timeline = ({ items, scale = 'month', onRowClick }: TimelineProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);
  const currentDate = useCurrentDate();
  const range = getTimelineRange(items, currentDate);
  const timelineGridSize = getTimelineGridSize(scale);

  useEffect(() => {
    setTimeout(() => {
      if (containerRef.current && markerRef.current) {
        const container = containerRef.current;
        const marker = markerRef.current;

        const containerCenter = container.offsetWidth / 2;
        const markerOffset = marker.offsetLeft + marker.offsetWidth / 2;

        container.scrollTo({
          left: markerOffset - containerCenter,
          behavior: 'smooth',
        });
      }
    }, 250);
  }, [items, scale]);

  const handleRowClick = (shipment: ShipmentModel) => {
    if (isFunction(onRowClick)) onRowClick(shipment);
  };

  const { base, scroller, leftShadow } = styles();

  return (
    <div className={base()} ref={containerRef}>
      <div className={leftShadow()} />
      <div className={scroller()}>
        <TimelineRangeBar
          range={range}
          currentDate={currentDate}
          scale={scale}
          gridSize={timelineGridSize}
          markerRef={markerRef}
        />
        <div className="absolute left-0 right-0 top-0 z-4 h-full pt-14">
          {items.map((item) => {
            return (
              <TimelineRow
                key={item.originalShipment.id}
                item={item}
                range={range}
                gridSize={timelineGridSize}
                onClick={handleRowClick}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'h-full overflow-x-auto overflow-y-hidden',
    scroller: 'relative h-full',
    leftShadow: 'absolute bottom-0 left-0 top-0 z-5 w-7 shadow-[inset_10px_0px_8px_-8px_rgba(0,0,0,0.08)]',
  },
});

export { Timeline };
